import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { API } from "@/lib/api";

const genId = () => (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`);

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: genId(), role: "assistant", content: "Namaste! I'm Bazaari. Ask me about products, categories, or shipping." }
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = { id: genId(), role: "user", content: input };
    const placeholderId = genId();
    setMessages((m) => [...m, userMsg, { id: placeholderId, role: "assistant", content: "" }]);
    const message = input;
    setInput("");
    setLoading(true);

    try {
      // Auth cookie is httpOnly; browser sends it automatically with credentials: include.
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, session_id: sessionId }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.session_id) setSessionId(data.session_id);
            if (data.delta) {
              setMessages((m) => m.map((msg) =>
                msg.id === placeholderId ? { ...msg, content: msg.content + data.delta } : msg
              ));
            }
          } catch (err) {
            console.error("Chat SSE parse failed:", err, line);
          }
        }
      }
    } catch (err) {
      console.error("Chat request failed:", err);
      setMessages((m) => m.map((msg) =>
        msg.id === placeholderId ? { ...msg, content: "Sorry, I'm offline right now. Please try again." } : msg
      ));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        data-testid="chatbot-toggle"
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#13294B] text-white shadow-lg hover:bg-[#1e3a63] transition-colors grid place-items-center"
      >
        {open ? <X className="w-5 h-5" /> : <MessageCircle className="w-5 h-5" />}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[360px] max-w-[calc(100vw-3rem)] h-[500px] bg-white border border-[#E2DCD0] rounded-2xl shadow-2xl flex flex-col overflow-hidden fade-up">
          <div className="p-4 border-b border-[#E2DCD0] bg-[#F2EFEB]">
            <div className="font-serif text-lg text-[#13294B]">Bazaari</div>
            <div className="label-caps">AI Support · Always Here</div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-[#E25A24] text-white rounded-br-sm" : "bg-[#F2EFEB] text-[#0D141C] rounded-bl-sm"}`}>
                  {m.content || (loading && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-[#E2DCD0] flex gap-2">
            <input
              data-testid="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about a product…"
              className="flex-1 px-3 py-2 rounded-full bg-[#F2EFEB] text-sm focus:outline-none border border-transparent focus:border-[#E25A24]"
            />
            <button data-testid="chatbot-send" onClick={send} disabled={loading} className="w-9 h-9 rounded-full bg-[#E25A24] text-white grid place-items-center disabled:opacity-50">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
